/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Tuesday, March 22nd 2022, 8:01:39 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */
import React from "react";
import { useTheme, Breakpoint, useMediaQuery } from "@mui/material";

interface ScreenProperties {
  screenBreakpoint: Breakpoint;
  screenHeight: number;
  screenWidth: number;
  deviceTypeGuess: "mobile" | "tablet" | "desktop" | "4kTV" | "unknown";
}

export const useGetScreenProperties = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.only("xs"));
  const isTablet = useMediaQuery(theme.breakpoints.only("sm"));
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const is4K = useMediaQuery(theme.breakpoints.up(3840));

  const getScreenBreakpoint = (screenWidth: number): Breakpoint =>
    Object.entries(theme.breakpoints.values)
      .filter((v) => Math.floor(screenWidth / v[1]) >= 1)
      .reverse()?.[0]?.[0] as Breakpoint;

  const guessDeviceType = React.useCallback((): ScreenProperties["deviceTypeGuess"] => {
    if (is4K) return "4kTV";
    if (isDesktop) return "desktop";
    if (isTablet) return "tablet";
    if (isMobile) return "mobile";
    return "unknown";
  }, [is4K, isDesktop, isTablet, isMobile]);

  const [screenProperties, SetScreenProperties] = React.useState<ScreenProperties>({
    screenBreakpoint: getScreenBreakpoint(window.innerWidth),
    screenHeight: window.innerHeight,
    screenWidth: window.innerWidth,
    deviceTypeGuess: "unknown",
  });

  const resizeObserver = React.useRef<ResizeObserver>();

  React.useEffect(() => {
    if (!resizeObserver.current) {
      resizeObserver.current = new ResizeObserver((entries) =>
        SetScreenProperties((prev) => ({
          ...prev,
          screenBreakpoint: getScreenBreakpoint(entries?.[0].contentRect.width),
          screenHeight: entries?.[0].contentRect.height,
          screenWidth: entries?.[0].contentRect.width,
        }))
      );
      resizeObserver.current.observe(document.body);
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    SetScreenProperties((prev) => ({
      ...prev,
      deviceTypeGuess: guessDeviceType(),
    }));
  }, [guessDeviceType]);

  return screenProperties;
};
